import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Radial from "../../Charts/Radial";
import timeToHireTypes from "../types/timeToHireTypes";
import { ChartTitle } from "../styles/employer-analytics-styles";
import AnalyticsWidgetLoading from "../../shared/loaders/AnalyticsWidgetLoading";
import {
  Wrapper,
  Charts,
  Chart,
  ChartName,
} from "../styles/time-to-hire-styles";
import { isFeatureEnabled } from "../../../helpers";

interface ITimeToHireProps {
  timeToHire: {
    yourCompany: number;
    hiringHub: number;
    industryAvg: number;
  };
  pdfExport?: boolean;
  pipelineSelected?: boolean;
  timeToHireWidgetLoader: boolean;
}

export const TimeToHire: FunctionComponent<ITimeToHireProps> = ({
  timeToHire,
  pdfExport,
  pipelineSelected,
  timeToHireWidgetLoader,
}) => {
  const max = 50;
  const valueToPercent = (value) => (value * 100) / max;

  const yourCompanyOptions = {
    series: [valueToPercent(timeToHire.yourCompany)],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "17px",
            },
            value: {
              formatter: (val) =>
                Math.ceil(parseInt(val, 10) / (100 / max)) || 0,
              color: "#111",
              offsetY: -35,
              fontSize: "24px",
              show: true,
            },
          },
        },
      },
      colors: ["rgb(0, 143, 251)"],
      labels: ["days"],
    },
  };

  const hiringHubTimeToHire = isFeatureEnabled("demoEnvironment")
    ? 21
    : timeToHire.hiringHub;

  const hiringHubOptions = {
    series: [valueToPercent(hiringHubTimeToHire)],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "17px",
            },
            value: {
              formatter: (val) =>
                Math.ceil(parseInt(val, 10) / (100 / max)) || 0,
              color: "#111",
              offsetY: -35,
              fontSize: "24px",
              show: true,
            },
          },
        },
      },
      colors: ["#f2008c"],
      labels: ["days"],
    },
  };

  const industryAvgOptions = {
    series: [valueToPercent(timeToHire.industryAvg)],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "17px",
            },
            value: {
              formatter: (val) =>
                Math.ceil(parseInt(val, 10) / (100 / max)) || 0,
              color: "#111",
              offsetY: -35,
              fontSize: "24px",
              show: true,
            },
          },
        },
      },
      colors: ["rgb(0, 227, 150)"],
      labels: ["days"],
    },
  };

  return (
    <Wrapper
      className="animated fadeIn delay-3"
      pdfExport={pdfExport}
      pipelineSelected={pipelineSelected}
      id="time-to-hire-widget"
    >
      <AnalyticsWidgetLoading
        text="Updating..."
        active={timeToHireWidgetLoader}
      />
      <ChartTitle marginBottom={5} data-test-id="time-to-hire-charts-title">
        Time to Hire (12 month average)
      </ChartTitle>
      <Charts data-test-id="time-to-hire-charts" pdfExport={pdfExport}>
        <Chart className="animated fadeIn delay-3">
          <Radial
            graphOptions={yourCompanyOptions}
            dataTestId="your-company-tth"
          />
          <ChartName>Your company</ChartName>
        </Chart>
        <Chart className="animated fadeIn delay-3">
          <Radial graphOptions={hiringHubOptions} dataTestId="hiring-hub-tth" />
          <ChartName>Hiring Hub</ChartName>
        </Chart>
        <Chart className="animated fadeIn delay-3">
          <Radial
            graphOptions={industryAvgOptions}
            dataTestId="industry-avg-tth"
          />
          <ChartName>Industry Average</ChartName>
        </Chart>
      </Charts>
    </Wrapper>
  );
};

TimeToHire.propTypes = {
  timeToHire: timeToHireTypes,
  pdfExport: PropTypes.bool,
  pipelineSelected: PropTypes.bool,
  timeToHireWidgetLoader: PropTypes.bool.isRequired,
};

TimeToHire.defaultProps = {
  pdfExport: null,
  pipelineSelected: null,
  timeToHire: {
    yourCompany: 0,
    hiringHub: 0,
    industryAvg: 0,
  },
};

interface IReduxState {
  employerAnalytics: {
    chartsData: {
      timeToHire: {
        yourCompany: number;
        hiringHub: number;
        industryAvg: number;
      };
    };
    timeToHireWidgetLoader: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  timeToHire: state.employerAnalytics.chartsData.timeToHire,
  timeToHireWidgetLoader: state.employerAnalytics.timeToHireWidgetLoader,
});

export default connect(mapStateToProps, {})(TimeToHire);
